// dashboardHistoricalPortfolioDiversification.tsx

import { useCallback, useMemo, useState } from 'react';
import { Box, Typography } from '@mui/material';
import { useNavigate } from 'react-router';

import { FooterButton } from '@/components/common/FooterButton';
import DateRange from '@/components/date/DateRange';
import AssetAllocationChart from '@/modules/portfolio/charts/assetAllocationChart';
import { StandardWidgetProps } from '@/models/WidgetModels';
import { useLocalization } from '@/hooks/useLocalization';
import { useCanNavigateTo } from '@/hooks/useCanNavigateTo';
import { useControlledDates } from '@/hooks/useControlledDates';
import { useWidgetWidthIsBelow } from '@/hooks/useWidgetWidthIsBelow';
import { DASHBOARD_WIDGET_DATE_PICKER_SX } from '@/constants/dashboardWidgetDatePickerSx';
import { DataRangeFilterEnum, getDateRangeFromFilter } from '@/constants/dates';
import { dashboardMobileWidthValue } from '@/constants';
import { paths } from '@/constants/paths';
import { parseAppDate } from '@/utils/date';
import dayjs from '@/config/dayjs';
import { ContentContainer } from '../contentContainer';

type DashboardHistoricalPortfolioDiversificationProps = StandardWidgetProps;

const isEmbeddableBundle = import.meta.env.VITE_WIDGET === 'external';

/** A dashboard tile is much shorter than the full Asset Allocation page. */
const WIDGET_CHART_HEIGHT = 360;

export default function DashboardHistoricalPortfolioDiversification({
  onDataLoaded,
  title,
  isWidget = false,
  startDate: propStartDate,
  endDate: propEndDate,
  excludedAccounts,
  excludedAccountTypes,
  excludedCategories,
  container = 'widget',
}: DashboardHistoricalPortfolioDiversificationProps) {
  const { t } = useLocalization();
  const widgetTitle = title ?? t('widgetHistoricalAssetAllocation');
  const navigate = useNavigate();
  const [isChartUpdating, setIsChartUpdating] = useState(true);

  const { startDate: defaultStartDate, endDate: defaultEndDate } = useMemo(
    () => getDateRangeFromFilter(DataRangeFilterEnum.OpenYear),
    [],
  );

  const handleChartUpdatingChange = useCallback((updating: boolean) => {
    setIsChartUpdating(updating);
  }, []);

  const { startDate, endDate, setStartDate, setEndDate } = useControlledDates({
    propStartDate,
    propEndDate,
    initialWidgetStartDate: defaultStartDate,
    initialWidgetEndDate: defaultEndDate,
    isWidget,
  });

  const {
    ref: widgetWidthRef,
    widthPx,
    isBelow: isWidgetBelowMobileWidth,
  } = useWidgetWidthIsBelow(isWidget, dashboardMobileWidthValue, 'border-box');

  const canViewDiversification = useCanNavigateTo(
    paths.investor.portfolio.assetAllocation,
  );

  const today = dayjs().startOf('day');

  const handleRangeChange = (newStartDate: string, newEndDate: string) => {
    const cleared = !newStartDate || !newEndDate;
    setStartDate(cleared ? defaultStartDate : newStartDate);
    setEndDate(cleared ? defaultEndDate : newEndDate);
  };

  const chartStartDate = parseAppDate(startDate)?.toDate();
  const chartEndDate = parseAppDate(endDate)?.toDate();
  const disableDatePicker = isEmbeddableBundle && isChartUpdating;

  return (
    <ContentContainer
      container={container}
      sx={{ textAlign: 'center' }}
      ref={widgetWidthRef}
    >
      <Box>
        <Box
          sx={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'flex-start',
            mb: 2,
          }}
        >
          <Typography variant='h5'>{widgetTitle}</Typography>
          {isWidget && (
            <Box sx={DASHBOARD_WIDGET_DATE_PICKER_SX}>
              <DateRange
                startDate={startDate}
                endDate={endDate}
                onChange={handleRangeChange}
                maxDate={today}
                useWidgetNarrowIconField={isWidgetBelowMobileWidth}
                disabled={disableDatePicker}
              />
            </Box>
          )}
        </Box>

        {chartStartDate && chartEndDate && (
          <AssetAllocationChart
            startDate={chartStartDate}
            endDate={chartEndDate}
            displayMode='dollar'
            onDataLoaded={onDataLoaded}
            onChartUpdatingChange={handleChartUpdatingChange}
            containerWidth={widthPx ?? undefined}
            height={WIDGET_CHART_HEIGHT}
            excludedAccounts={excludedAccounts}
            excludedAccountTypes={excludedAccountTypes}
            excludedCategories={excludedCategories}
          />
        )}
      </Box>
      {canViewDiversification && !isEmbeddableBundle && (
        <FooterButton
          buttonText={t('viewFullAllocationDetails')}
          onClick={() => {
            void navigate(paths.investor.portfolio.assetAllocation);
          }}
        />
      )}
    </ContentContainer>
  );
}
